import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Pause, Play } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { PageHeader, StatusPill, Waveform } from "@/components/bits";

const TERMINAL = ["converted", "not_converted", "declined", "unreachable", "opted_out"];
const FILTERS = [["all", "All"], ["in_progress", "In progress"], ["terminal", "Terminal"], ["converted", "Converted"]];
const channelLabel = (ch) => (ch === "whatsapp" ? "WhatsApp" : ch === "voice_call" || ch === "call" ? "Call" : ch === "sms" ? "SMS" : ch || "—");
const relTime = (iso) => {
  if (!iso) return "";
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return timeAgo(iso);
  const m = Math.round(diff / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.round(m / 60);
  return h < 48 ? `in ${h}h` : `in ${Math.round(h / 24)}d`;
};

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openLead, setOpenLead] = useState(null);

  const loadCampaign = useCallback(() => api.get(`/campaigns/${id}`).then((r) => setC(r.data)).catch(() => {}), [id]);
  const loadLeads = useCallback(() => api.get(`/campaigns/${id}/leads`).then((r) => setLeadData(r.data)).catch(() => {}), [id]);
  useEffect(() => {
    loadCampaign(); loadLeads();
    const t = setInterval(() => { loadCampaign(); loadLeads(); }, 3000);
    return () => clearInterval(t);
  }, [loadCampaign, loadLeads]);

  const ctl = async (action) => {
    try {
      await api.post(`/campaigns/${id}/${action}`);
      toast.success(action === "pause" ? "Campaign paused" : action === "resume" ? "Campaign resumed" : "Campaign started");
      loadCampaign();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!c) return <p className="text-slate-500 text-sm">Loading…</p>;

  const leadType = c.lead_type || c.audience_type || "farmer";
  const region = [c.region?.state || c.state, (c.region?.districts || []).join(", ")].filter(Boolean).join(" · ");
  const sm = c.success_metric || {};
  const counts = leadData?.counts || {};
  const leads = leadData?.leads || [];
  const conversations = c.conversations || [];
  const target = sm.target ?? 0;
  const current = sm.current ?? 0;
  const goalPct = target ? Math.min(100, Math.round((current / target) * 100)) : 0;
  const processedPct = c.total ? Math.min(100, Math.round(((c.processed ?? 0) / c.total) * 100)) : 0;

  const shown = leads.filter((l) => {
    if (filter === "in_progress") return l.status === "in_progress" || l.status === "queued";
    if (filter === "terminal") return TERMINAL.includes(l.status);
    if (filter === "converted") return l.status === "converted" || l.goal_status === "achieved";
    return true;
  });

  const openRow = (l) => {
    if (l.last_run?.conversation_id) { nav(`/dashboard/conversations/${l.last_run.conversation_id}`); return; }
    setOpenLead((prev) => (prev === l.id ? null : l.id));
  };

  return (
    <div>
      <PageHeader overline={<Link to="/dashboard/campaigns" className="hover:text-emerald-200">← Campaigns</Link>} title={c.name} testId="campaign-detail-header"
        desc={`${c.agent_name ? `${c.agent_name} · ` : ""}${leadType} · ${region || "no region"} · ${(c.channels || ["whatsapp", "call"]).map(channelLabel).join(" → ")}`}
        action={<div className="flex items-center gap-2 flex-wrap"><StatusPill status={c.status} />
          {c.status === "running" ? <button onClick={() => ctl("pause")} className="btn-ghost btn-sm" data-testid="campaign-pause-detail"><Pause className="w-3.5 h-3.5" /> Pause</button>
            : c.status === "paused" ? <button onClick={() => ctl("resume")} className="btn-primary btn-sm" data-testid="campaign-resume-detail"><Play className="w-3.5 h-3.5" /> Resume</button>
              : <button onClick={() => ctl("run")} className="btn-primary btn-sm" data-testid="campaign-run-detail"><Play className="w-3.5 h-3.5" /> {c.status === "completed" ? "Re-run" : "Run"}</button>}
        </div>} />

      {c.status === "running" && <div className="glass p-4 mb-5 flex items-center gap-4 rise" data-testid="campaign-progress"><Waveform bars={9} /><div className="flex-1"><p className="text-sm text-slate-200">Agents are on calls… {c.processed ?? 0}/{c.total ?? 0} leads processed</p><div className="h-1.5 rounded-full bg-white/10 mt-2 overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-amber-400 transition-[width] duration-700" style={{ width: `${processedPct}%` }} /></div></div></div>}

      <div className="glass p-5 mb-5 rise" data-testid="campaign-success-card">
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-1 min-w-0">
            <p className="overline-label mb-1">Success metric · goal conversions</p>
            <p className="font-display text-3xl font-bold text-emerald-300">{current}<span className="text-slate-500 text-xl">/{target || "—"}</span> <span className="text-xs font-normal text-slate-500">{goalPct}% of target</span></p>
            {c.goal && <p className="text-xs text-slate-400 mt-1.5"><span className="text-slate-500 font-mono uppercase text-[10px]">Goal:</span> {c.goal}</p>}
            <div className="h-2 rounded-full bg-white/10 mt-3 overflow-hidden max-w-xl"><div className="h-full bg-gradient-to-r from-emerald-500 to-amber-400 transition-[width] duration-700" style={{ width: `${goalPct}%` }} /></div>
          </div>
          <div className="grid grid-cols-3 gap-5 text-center">
            {[["POs", c.orders ?? 0, "cstat-orders"], ["Reached", c.reached ?? counts.terminal ?? 0, "cstat-reach"], ["Converted", counts.converted ?? 0, "cstat-converted"]].map(([label, value, tid]) => (
              <div key={label}><p className="text-[10px] font-mono uppercase text-slate-500">{label}</p><p className="font-display text-xl font-bold text-slate-100" data-testid={tid}>{value}</p></div>
            ))}
          </div>
        </div>
      </div>

      <div className="glass overflow-hidden mb-5 rise" data-testid="campaign-leads-table">
        <div className="px-5 pt-4 pb-3 flex flex-col sm:flex-row sm:items-center gap-3 border-b border-white/5">
          <div className="flex-1">
            <p className="font-display font-semibold text-slate-100">Leads</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{counts.pending ?? 0} pending · {counts.queued ?? 0} queued · {counts.in_progress ?? 0} in progress · {counts.terminal ?? 0} terminal</p>
          </div>
          <div className="flex gap-1 p-1 glass-soft rounded-full flex-wrap">
            {FILTERS.map(([k, label]) => <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs ${filter === k ? "bg-white/10 text-slate-50" : "text-slate-400"}`} data-testid={`campaign-filter-${k}`}>{label}</button>)}
          </div>
        </div>
        <div className="overflow-x-auto"><table className="w-full min-w-[980px]"><thead><tr><th className="table-head">Name</th><th className="table-head">Phone</th><th className="table-head">District / block</th><th className="table-head">Retailer tag</th><th className="table-head">Status</th><th className="table-head">Goal</th><th className="table-head">Attempts</th><th className="table-head">Next action</th><th className="table-head">Last run</th></tr></thead>
          <tbody>
            {shown.map((l) => (
              <LeadRows key={l.id} lead={l} open={openLead === l.id} onOpen={() => openRow(l)} />
            ))}
            {!shown.length && <tr><td colSpan="9" className="table-cell text-center text-slate-500 py-10">{leads.length ? "No leads match this filter." : c.status === "running" ? "Leads are being worked…" : "No leads loaded. Add a lead sheet to start outreach."}</td></tr>}
          </tbody></table></div>
      </div>

      <div className="glass overflow-hidden rise" data-testid="campaign-conversations">
        <div className="px-5 pt-4 pb-1"><p className="font-display font-semibold text-slate-100">Conversations</p><p className="text-[11px] text-slate-500 mt-0.5">Transcripts written by this campaign's calls.</p></div>
        <div className="overflow-x-auto"><table className="w-full min-w-[760px]"><thead><tr><th className="table-head">Contact</th><th className="table-head">Location</th><th className="table-head">Crop / stage</th><th className="table-head">Outcome</th><th className="table-head">Duration</th><th className="table-head"></th></tr></thead>
          <tbody>{conversations.map((cv) => <tr key={cv.id} className="hover:bg-white/[0.02]" data-testid={`campaign-conv-${cv.id}`}>
            <td className="table-cell"><p className="text-slate-100">{cv.contact?.name}</p><p className="text-xs text-slate-500">{cv.contact?.phone}</p></td>
            <td className="table-cell text-xs">{cv.contact?.sub_district || cv.district}, {cv.state}</td>
            <td className="table-cell text-xs">{cv.farm ? <>{cv.farm.crop} · {cv.farm.stage} {cv.cropping_confirmed === false && <span className="pill-gold ml-1">corrected</span>}</> : "—"}</td>
            <td className="table-cell"><StatusPill status={cv.outcome} /></td>
            <td className="table-cell font-mono text-xs">{cv.duration_sec ? `${Math.floor(cv.duration_sec / 60)}:${String(cv.duration_sec % 60).padStart(2, "0")}` : "—"}</td>
            <td className="table-cell"><Link to={`/dashboard/conversations/${cv.id}`} className="btn-ghost btn-sm" data-testid={`open-conv-${cv.id}`}>Open</Link><span className="text-[10px] text-slate-600 ml-2">{timeAgo(cv.created_at)}</span></td>
          </tr>)}
          {!conversations.length && <tr><td colSpan="7" className="table-cell text-center text-slate-500 py-10">{c.status === "running" ? "First conversations arriving…" : "No conversations yet. Run the campaign."}</td></tr>}</tbody></table></div>
      </div>
    </div>
  );
}

function LeadRows({ lead: l, open, onOpen }) {
  const meta = Object.entries(l.meta_data || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  const lastRun = l.last_run;
  const pa = l.pending_action;
  const callbackActive = pa && (pa.status === "pending" || pa.status === "running");
  return (
    <>
      <tr className="hover:bg-white/[0.03] cursor-pointer" onClick={onOpen} data-testid={`campaign-lead-row-${l.id}`} title={lastRun?.conversation_id ? "Open the conversation" : "Show goal reasoning"}>
        <td className="table-cell text-slate-100">{l.name}</td>
        <td className="table-cell font-mono text-xs">{l.phone}</td>
        <td className="table-cell text-xs">{[l.sub_district, l.district].filter(Boolean).join(", ") || "—"}</td>
        <td className="table-cell text-xs">{l.retailer_name ? <span className="pill-muted" title={l.retailer_id}>{l.retailer_name}</span> : "—"}</td>
        <td className="table-cell"><StatusPill status={l.status} /></td>
        <td className="table-cell text-xs">{l.goal_status === "achieved" ? <span className="text-emerald-300" title={l.goal_reasoning}>✓ achieved</span> : l.goal_status === "not_achieved" ? <span className="text-rose-300" title={l.goal_reasoning}>✗ not achieved</span> : <span className="text-slate-500">—</span>}</td>
        <td className="table-cell font-mono text-xs">{l.attempts ?? 0}</td>
        <td className="table-cell text-xs text-slate-400">
          {callbackActive ? (
            <span className="pill-cyan" title={pa.reason || pa.time_text || "Callback requested"} data-testid={`lead-callback-${l.id}`}>
              Callback {relTime(pa.scheduled_for)}
            </span>
          ) : l.next_action_at ? relTime(l.next_action_at) : (TERMINAL.includes(l.status) ? "—" : timeAgo(l.last_contact_at) || "—")}
        </td>
        <td className="table-cell">{lastRun ? <><span className="text-xs text-slate-200">{channelLabel(lastRun.channel)} · {lastRun.status}</span>{lastRun.reason && <p className="text-[11px] text-slate-500 truncate max-w-[220px]" title={lastRun.reason}>{lastRun.reason}</p>}</> : <span className="text-xs text-slate-600">—</span>}</td>
      </tr>
      {open && (
        <tr className="bg-white/[0.02]" data-testid={`campaign-lead-detail-${l.id}`}>
          <td colSpan="9" className="table-cell">
            <p className="text-xs text-slate-300">{l.goal_reasoning || "No goal reasoning recorded yet."}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {meta.map(([k, v]) => <span key={k} className="pill-muted">{k}: {String(v)}</span>)}
              {lastRun?.created_at && <span className="pill-muted">last run: {channelLabel(lastRun.channel)} · {timeAgo(lastRun.created_at)}</span>}
              {pa && <span className={callbackActive ? "pill-cyan" : "pill-muted"} data-testid={`lead-callback-detail-${l.id}`}>callback: {pa.status}{pa.scheduled_for ? ` · ${relTime(pa.scheduled_for)}` : ""}{pa.reason ? ` · ${pa.reason}` : ""}{pa.last_error ? ` · ${pa.last_error}` : ""}</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
